import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  QueueListIcon,
  CpuChipIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  ArrowPathIcon,
  BoltIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'sonner';
import adminService from '../../services/admin.service';
import { AdminSystemHealth } from '../../types/admin.types';
import ConfirmationDialog from '../../components/shared/ConfirmationDialog';

const AdminSystem: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<AdminSystemHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [confirmAction, setConfirmAction] = useState<'retry' | 'unstick' | null>(null);

  const fetchData = async () => {
    try {
      const health = await adminService.getSystemHealth();
      setData(health);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleRefresh = () => {
    setLoading(true);
    fetchData();
  };

  const handleRetryAllFailed = async () => {
    setActionLoading(true);
    try {
      const result = await adminService.retryFailedAnalyses();
      toast.success(result.message);
      setConfirmAction(null);
      fetchData();
    } catch {
      toast.error('Failed to retry analyses');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnstickProcessing = async () => {
    setActionLoading(true);
    try {
      const result = await adminService.unstickProcessing();
      toast.success(result.message);
      setConfirmAction(null);
      fetchData();
    } catch {
      toast.error('Failed to unstick analyses');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-48 bg-gray-200 rounded" />
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 bg-white rounded-xl shadow" />
          ))}
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="rounded-md bg-red-50 p-4">
        <p className="text-sm text-red-700">Failed to load system health data</p>
      </div>
    );
  }

  const getQueueAge = (): string => {
    if (!data.oldest_queued_at) return 'No items queued';
    const age = Date.now() - new Date(data.oldest_queued_at).getTime();
    const minutes = Math.floor(age / 60000);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${minutes % 60}m ago`;
  };

  const isQueueStale = (): boolean => {
    if (!data.oldest_queued_at) return false;
    const age = Date.now() - new Date(data.oldest_queued_at).getTime();
    return age > 30 * 60000; // > 30 minutes
  };

  const pipelineCards = [
    {
      name: 'Queued',
      value: data.analyses_queued,
      icon: QueueListIcon,
      color: 'text-yellow-600',
      bg: 'bg-yellow-50',
      href: '/admin/analyses?status=queued',
    },
    {
      name: 'Processing',
      value: data.analyses_processing,
      icon: CpuChipIcon,
      color: 'text-blue-600',
      bg: 'bg-blue-50',
      href: '/admin/analyses?status=processing',
    },
    {
      name: 'Completed (24h)',
      value: data.analyses_completed_24h,
      icon: CheckCircleIcon,
      color: 'text-green-600',
      bg: 'bg-green-50',
      href: '/admin/analyses?status=completed',
    },
    {
      name: 'Failed (24h)',
      value: data.analyses_failed_24h,
      icon: ExclamationTriangleIcon,
      color: data.analyses_failed_24h > 0 ? 'text-red-600' : 'text-gray-600',
      bg: data.analyses_failed_24h > 0 ? 'bg-red-50' : 'bg-gray-50',
      href: '/admin/analyses?status=failed',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">System Health</h1>
          <p className="mt-1 text-sm text-gray-500">Analysis pipeline and ingestion status</p>
        </div>
        <button
          onClick={handleRefresh}
          className="inline-flex items-center gap-2 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
        >
          <ArrowPathIcon className="h-4 w-4" />
          Refresh
        </button>
      </div>

      {/* Analysis Pipeline */}
      <div>
        <h2 className="text-lg font-medium text-gray-900 mb-3">Analysis Pipeline</h2>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {pipelineCards.map((card) => (
            <button
              key={card.name}
              onClick={() => navigate(card.href)}
              className="overflow-hidden rounded-xl bg-white shadow hover:shadow-md transition-shadow cursor-pointer text-left w-full"
            >
              <div className="p-5">
                <div className="flex items-center">
                  <div className={`flex-shrink-0 rounded-md ${card.bg} p-3`}>
                    <card.icon className={`h-6 w-6 ${card.color}`} />
                  </div>
                  <div className="ml-5">
                    <p className="text-sm font-medium text-gray-500">{card.name}</p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {card.value.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Processing Details + Quick Actions */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <div className="rounded-xl bg-white shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Processing Performance</h3>
          <dl className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 rounded-md bg-indigo-50 p-3">
                <ClockIcon className="h-6 w-6 text-indigo-600" />
              </div>
              <div>
                <dt className="text-sm text-gray-500">Avg Processing Time (24h)</dt>
                <dd className="text-xl font-semibold text-gray-900">
                  {data.avg_processing_time_seconds !== null
                    ? `${data.avg_processing_time_seconds.toFixed(1)}s`
                    : 'No data'}
                </dd>
              </div>
            </div>
            <div>
              <dt className="text-sm text-gray-500 mb-1">Oldest Queued Analysis</dt>
              <dd className={`text-sm font-medium ${isQueueStale() ? 'text-red-600' : 'text-gray-900'}`}>
                {getQueueAge()}
                {isQueueStale() && (
                  <span className="ml-2 inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
                    Stale
                  </span>
                )}
              </dd>
            </div>
          </dl>
        </div>

        <div className="rounded-xl bg-white shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Ingestion Pipeline</h3>
          <dl className="space-y-4">
            <div className="flex justify-between items-center">
              <dt className="text-sm text-gray-500">Active Jobs</dt>
              <dd className="text-xl font-semibold text-gray-900">{data.ingestion_jobs_active}</dd>
            </div>
            <div className="flex justify-between items-center">
              <dt className="text-sm text-gray-500">Failed Jobs (24h)</dt>
              <dd className={`text-xl font-semibold ${data.ingestion_jobs_failed_24h > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                {data.ingestion_jobs_failed_24h}
              </dd>
            </div>
          </dl>
        </div>

        {/* Quick Actions */}
        <div className="rounded-xl bg-white shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
            <BoltIcon className="h-5 w-5 text-amber-500" />
            Quick Actions
          </h3>
          <div className="space-y-3">
            <button
              onClick={() => setConfirmAction('retry')}
              disabled={data.analyses_failed_24h === 0}
              className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-white px-4 py-2.5 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowPathIcon className="h-4 w-4 text-red-500" />
              Retry All Failed ({data.analyses_failed_24h})
            </button>
            <button
              onClick={() => setConfirmAction('unstick')}
              disabled={!isQueueStale() && data.analyses_processing === 0}
              className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-white px-4 py-2.5 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <CpuChipIcon className="h-4 w-4 text-blue-500" />
              Unstick Processing
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={confirmAction !== null}
        onClose={() => setConfirmAction(null)}
        onConfirm={confirmAction === 'retry' ? handleRetryAllFailed : handleUnstickProcessing}
        title={confirmAction === 'retry' ? 'Retry All Failed Analyses' : 'Unstick Processing Analyses'}
        message={
          confirmAction === 'retry'
            ? `This will retry ${data.analyses_failed_24h} failed analyses from the last 24 hours.`
            : 'This will reset analyses stuck in processing for more than 30 minutes back to the queue.'
        }
        variant="warning"
        loading={actionLoading}
      />
    </div>
  );
};

export default AdminSystem;
